import React, { useEffect, useState } from "react";
import CallMadeIcon from "@mui/icons-material/CallMade";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlined";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import { SoftphoneSipConfig, UserPermissions } from "../hooks/useSoftphoneConfig";
import { useMakeCall } from "../hooks/useMakeCall";
import { CallHistoryRecord, fetchCallHistory, PabxRequestError } from "../utils/pabxApi";
import { DialConfirmationDialog, DialIconButton } from "./DialControls";
import CallRecordingPlayer from "./CallRecordingPlayer";
import { useTranslation } from "../i18n/LocaleContext";
import { SupportedLocale } from "../i18n/locale";

interface CallHistoryPanelProps {
  sipConfig: SoftphoneSipConfig | null;
  utechApiToken: string;
  permissions: UserPermissions | null;
  onPlaceCall: (number: string, variaveis?: string) => void;
  refreshKey?: number;
  className?: string;
}

function formatDateTime(date: Date | null, raw: string, locale: SupportedLocale) {
  if (!date) return raw || "---";
  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(totalSeconds: number) {
  if (!totalSeconds || totalSeconds < 0) return "0:00";
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${minutes}:${pad(seconds)}`;
}

function sanitizeFileNamePart(value: string) {
  return value.replace(/[^\w.-]+/g, "_");
}

function buildRecordingFileName(record: CallHistoryRecord) {
  return `gravacao_${sanitizeFileNamePart(record.counterpart || "chamada")}_${sanitizeFileNamePart(
    record.startRaw || record.id
  )}.wav`;
}

function DirectionIcon({ record, t }: { record: CallHistoryRecord; t: (key: any) => string }) {
  if (record.direction === "out") {
    return <CallMadeIcon titleAccess={t("callHistory.direction.outbound")} style={{ color: "#0c5a35", fontSize: 20 }} />;
  }
  return <CallReceivedIcon titleAccess={t("callHistory.direction.inbound")} style={{ color: "#c0392b", fontSize: 20 }} />;
}

const CallHistoryPanel: React.FC<CallHistoryPanelProps> = ({ sipConfig, utechApiToken, permissions, onPlaceCall, refreshKey, className }) => {
  // Backend antigo (permissions ausente) mantém o acesso liberado.
  const canPlayRecordings = permissions?.recordAccess ?? true;
  const { t, locale } = useTranslation();
  const [records, setRecords] = useState<CallHistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { selectedNumber, dialLoading, dialError, requestDial, cancelDial, confirmDial } = useMakeCall(
    sipConfig,
    onPlaceCall
  );

  const domain = sipConfig?.domain || "";
  const extension = sipConfig?.extension || "";

  useEffect(() => {
    if (!sipConfig || !extension || !domain || !utechApiToken) {
      setRecords([]);
      setLoading(false);
      setError(t("callHistory.error.configNotReady"));
      return;
    }

    const controller = new AbortController();

    async function loadHistory() {
      try {
        setLoading(true);
        setError("");
        const results = await fetchCallHistory(sipConfig as SoftphoneSipConfig, utechApiToken, t, {
          days: 30,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setRecords(results);
      } catch (err) {
        if (controller.signal.aborted) return;
        setRecords([]);
        // Mensagem do erro nunca vai à tela: nas chamadas diretas ao PABX ela pode conter a
        // URL da requisição, que leva o token na query string. Só o status HTTP é exibido.
        setError(
          err instanceof PabxRequestError
            ? t("callHistory.error.loadFailedWithCode", { code: err.status })
            : t("callHistory.error.loadFailedGeneric")
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadHistory();
    return () => controller.abort();
  }, [domain, extension, sipConfig, utechApiToken, refreshKey, t]);

  return (
    <article className={`status-card call-history-card${className ? ` ${className}` : ""}`}>
      <div className="card-header">
        <h2>{t("callHistory.title")}</h2>
      </div>
      <div className="call-search-panel-body">
        <div style={{ marginBottom: 12 }}>
          <small>{t("callHistory.subtitle", { extension: extension || "---" })}</small>
        </div>
        <div className="call-search-results" aria-live="polite">
          {loading && (
            <p className="call-search-placeholder">{t("callHistory.loading")}</p>
          )}
          {!loading && !!error && (
            <p className="call-search-placeholder call-search-placeholder-error">{error}</p>
          )}
          {!loading && !error && records.length === 0 && (
            <p className="call-search-placeholder">{t("callHistory.empty")}</p>
          )}
          {!loading && !error && records.length > 0 && (
            <div className="call-history-list">
              {records.map((record) => (
                <article className="call-history-item" key={record.id}>
                  <div className="call-history-item-main">
                    <span className="call-history-item-direction">
                      <DirectionIcon record={record} t={t} />
                    </span>
                    <span className="call-history-item-number">
                      <DialIconButton number={record.counterpart} onDial={requestDial} />
                    </span>
                    <span className="call-history-item-actions">
                      {canPlayRecordings && record.recordingUrl && (
                        <IconButton
                          size="small"
                          className="call-history-record-trigger"
                          aria-label={
                            expandedId === record.id
                              ? t("callHistory.recording.hide")
                              : t("callHistory.recording.show")
                          }
                          onClick={() =>
                            setExpandedId((prev) => (prev === record.id ? null : record.id))
                          }
                        >
                          <PlayCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </span>
                    <span className="call-history-item-date">
                      {formatDateTime(record.startedAt, record.startRaw, locale)}
                    </span>
                    <span className="call-history-item-duration">
                      {formatDuration(record.durationSeconds)}
                    </span>
                  </div>
                  {canPlayRecordings && record.recordingUrl && (
                    <Collapse in={expandedId === record.id} unmountOnExit>
                      <CallRecordingPlayer
                        url={record.recordingUrl}
                        fileName={buildRecordingFileName(record)}
                      />
                    </Collapse>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
      <DialConfirmationDialog
        sipConfig={sipConfig}
        selectedNumber={selectedNumber}
        dialLoading={dialLoading}
        dialError={dialError}
        onCancel={cancelDial}
        onConfirm={confirmDial}
      />
    </article>
  );
};

export default CallHistoryPanel;
